import { LanguageCode } from "../config/languages";

/**
 * 다국어 문자열 타입
 * 각 언어별로 문자열을 저장하는 객체
 */
export type MultilingualString = Partial<Record<LanguageCode, string>>;

/**
 * 다국어 문자열에서 현재 언어의 값을 가져오는 헬퍼 함수
 * @param multilingual - 다국어 문자열 객체
 * @param currentLanguage - 현재 선택된 언어
 * @param fallbackLanguage - 대체 언어 (기본값: 'ko')
 * @returns 현재 언어의 문자열 또는 대체 언어의 문자열
 */
export function getLocalizedString(
  multilingual: MultilingualString,
  currentLanguage: LanguageCode,
  fallbackLanguage: LanguageCode = "ko",
): string {
  // 현재 언어의 값이 있으면 반환
  if (multilingual[currentLanguage]) {
    return multilingual[currentLanguage]!;
  }

  // 대체 언어의 값이 있으면 반환
  if (multilingual[fallbackLanguage]) {
    return multilingual[fallbackLanguage]!;
  }

  // 아무것도 없으면 빈 문자열 반환
  return "";
}

/**
 * 다국어 문자열이 비어있는지 확인하는 헬퍼 함수
 * @param multilingual - 다국어 문자열 객체
 * @returns 모든 언어의 값이 비어있으면 true
 */
export function isMultilingualStringEmpty(
  multilingual: MultilingualString,
): boolean {
  return Object.values(multilingual).every(
    (value) => !value || value.trim() === "",
  );
}

/**
 * 다국어 문자열에서 사용 가능한 언어 목록을 가져오는 헬퍼 함수
 * @param multilingual - 다국어 문자열 객체
 * @returns 값이 있는 언어 코드 배열
 */
export function getAvailableLanguages(
  multilingual: MultilingualString,
): LanguageCode[] {
  return Object.entries(multilingual)
    .filter(([_, value]) => value && value.trim() !== "")
    .map(([code, _]) => code as LanguageCode);
}
